import {
  ActionValidation,
  ConfigOptionValidation,
  StringValidation,
} from '../../../../utils/component-validations';
import { fn } from '@ember/helper';
import { isEmpty } from '@ember/utils';
import { on } from '@ember/modifier';
import { or } from 'ember-truth-helpers';
import CloseButton from '../../action/button/close.gjs';
import FlexRow from '../../layout/flex/row.gjs';
import GhostButton from '../../action/button/ghost.gjs';
import Super from '../../core/base.gjs';
import styles from './message.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

const ERROR = 'error';
const SUCCESS = 'success';
const SYSTEM = 'system';
const SUPPORTED_TYPES = [ERROR, SUCCESS, SYSTEM];

export const ALIGN_ITEMS_DEFAULT = 'center';
export const FLEX_DEFAULT = '1 0 auto';
export const GAP_DEFAULT = '16px';
export const JUSTIFY_CONTENT_DEFAULT = 'space-between';

export default class SpdsFeedbackToastMessage extends Super {
  arguments = [
    new ConfigOptionValidation({
      required: [
        {
          name: 'toast.type',
          options: SUPPORTED_TYPES,
        },
      ],
    }),
    new StringValidation({ required: ['toast.message'] }),
  ];
  baseClass = 'spds-toast-message spds-feedback-toast-message';
  classesToMapToAttrs = ['hasCta', 'type'];
  componentName = 'Spds::Feedback::Toast::Message';
  styles = styles;

  get alignItems() {
    return this.toast.alignItems ?? ALIGN_ITEMS_DEFAULT;
  }

  get flex() {
    return this.toast.flex ?? FLEX_DEFAULT;
  }

  get gap() {
    return this.toast.gap ?? GAP_DEFAULT;
  }

  get hasCta() {
    return !isEmpty(this.toast.cta);
  }

  get justifyContent() {
    return this.args.justifyContent ?? JUSTIFY_CONTENT_DEFAULT;
  }

  get toast() {
    return this.args.toast;
  }

  get type() {
    return this.toast.type;
  }

  get icon() {
    let { type } = this;
    let icon;

    switch (type) {
      case ERROR:
        icon = 'triangle-exclamation-mark';
        break;
      case SUCCESS:
        icon = 'circle-check';
        break;
      case SYSTEM:
        icon = 'circle-i';
        break;
      default:
        icon = null;
    }

    return icon;
  }

  constructor() {
    super(...arguments);

    let { cta } = this.args;

    if (cta) {
      this.arguments.push(
        new ActionValidation({ required: ['cta.action'] }),
        new StringValidation({ required: ['cta.text'] })
      );
    }
  }

  ctaClick = async () => {
    let shouldClose = await Promise.resolve(this.toast.cta.action?.());

    if (shouldClose || isEmpty(shouldClose)) {
      this.args.onManualDismiss?.(this.toast);
    }
  };

  <template>
    <FlexRow
      @alignItems={{this.alignItems}}
      @flex={{this.flex}}
      @gap={{this.gap}}
      @justifyContent={{this.justifyContent}}
      @tag="li"
      aria-live="assertive"
      class={{this.classes}}
      data-test-spds-toast-message={{or @index "0"}}
      data-test-spds-feedback-toast-message={{or @index "0"}}
      role="status"
      {{this.validateArguments}}
      ...attributes
    >
      {{#if this.icon}}
        {{svgJar
          this.icon
          class="icon"
          data-test-spds-toast-message-icon=""
          data-test-spds-feedback-toast-message-icon=""
        }}
      {{/if}}
      <FlexRow
        @flex="1 0 auto"
        class="text"
        data-test-spds-toast-message-text
        data-test-spds-feedback-toast-message-text
      >
        {{@toast.message}}
      </FlexRow>
      {{#if @toast.cta}}
        <GhostButton
          class="cta-btn"
          data-test-spds-toast-message-cta-button
          data-test-spds-feedback-toast-message-cta-button
          type="button"
          {{on "click" this.ctaClick}}
        >
          {{@toast.cta.text}}
        </GhostButton>
      {{/if}}
      <CloseButton
        @close={{fn @onManualDismiss @toast}}
        class="dismissal-btn"
        data-test-spds-toast-message-close-button
        data-test-spds-feedback-toast-message-close-button
      />
    </FlexRow>
  </template>
}
